import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, CheckCircle, AlertTriangle, AlertCircle, RefreshCw,
  Sparkles, Wallet, Users as UsersIcon, FileCheck, Calendar,
  TrendingUp, TrendingDown, Clock, Eye, X, Archive, ChevronRight,
  CheckSquare, Square, MessageCircle, Send, Trash2, AtSign, ShieldCheck, ShieldAlert,
  ChevronDown, Wrench, Download, Undo2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useRealtime } from '../context/RealtimeContext';
import {
  readLocalTicks, toggleTicks, hydrateAndSync, derivePeriodKey,
} from '../lib/recon_ticks';

const PERIOD_PRESETS = [
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_30', label: 'Last 30 Days' },
  { value: 'last_90', label: 'Last 90 Days' },
  { value: 'all', label: 'All Time' },
  { value: 'custom', label: 'Custom Range…' },
];

const formatINR = (n) => {
  const v = Number(n) || 0;
  return `₹${Math.round(Math.abs(v)).toLocaleString('en-IN')}${v < 0 ? '' : ''}`;
};
const formatINRSigned = (n) => {
  const v = Number(n) || 0;
  return `${v < 0 ? '-' : v > 0 ? '+' : ''}₹${Math.round(Math.abs(v)).toLocaleString('en-IN')}`;
};

export default function ReconcilePage() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [preset, setPreset] = useState('this_month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [health, setHealth] = useState(null);
  const [members, setMembers] = useState([]);
  const [fundLedger, setFundLedger] = useState(null);
  const [memberLedger, setMemberLedger] = useState(null);
  const [selectedView, setSelectedView] = useState('fund'); // 'fund' or member id
  const [anomalies, setAnomalies] = useState({ deterministic: [], ai: [], ai_used: false });
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completeNote, setCompleteNote] = useState('');
  const [completeBusy, setCompleteBusy] = useState(false);
  const [dismissTarget, setDismissTarget] = useState(null);
  const [dismissReason, setDismissReason] = useState('');
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotDetail, setSnapshotDetail] = useState(null);  // null or full snapshot doc
  const [loadingSnapshot, setLoadingSnapshot] = useState(false);
  const [ticks, setTicks] = useState(new Set());
  const [lastTickedRow, setLastTickedRow] = useState(null);  // for shift-click range selection
  const [activeReviewers, setActiveReviewers] = useState([]);  // co-admins currently on this period
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [adminList, setAdminList] = useState([]);  // for @mention autocomplete
  const [integrityStatus, setIntegrityStatus] = useState(null); // {ok, drift_fund, drift_coins, checked_at, trigger, acknowledged}
  const [ackBusy, setAckBusy] = useState(false); // iter237 — "Ignore drift" action
  const [ackAllBusy, setAckAllBusy] = useState(false); // iter271 — accept all member drifts
  const [memberAckBusy, setMemberAckBusy] = useState(false); // iter271 — per-member accept/undo
  const [traceOpen, setTraceOpen] = useState(false); // iter148 — drift banner trace panel
  const [integrityBusy, setIntegrityBusy] = useState(false);
  const [autoFixBusy, setAutoFixBusy] = useState(false); // iter149 — auto-fix in flight
  const [autoFixPreview, setAutoFixPreview] = useState(null); // iter149 — dry-run preview {fund_before, fund_after, fund_delta, coins_changed[]}
  const [ledgerExportBusy, setLedgerExportBusy] = useState(false); // iter151 — debug export
  const [reverseTarget, setReverseTarget] = useState(null); // iter152 — row to reverse {ref_id, description, type, dr, cr}
  const [reverseReason, setReverseReason] = useState('');
  const [reverseBusy, setReverseBusy] = useState(false);
  const { onEvent } = useRealtime();

  const periodParams = useMemo(() => {
    if (preset === 'custom') {
      const params = {};
      if (customFrom) params.from = `${customFrom}T00:00:00+00:00`;
      if (customTo) params.to = `${customTo}T23:59:59+00:00`;
      return params;
    }
    return { preset };
  }, [preset, customFrom, customTo]);

  const periodKey = useMemo(
    () => derivePeriodKey({ preset, from: periodParams.from, to: periodParams.to }),
    [preset, periodParams.from, periodParams.to]
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [groupResp, healthResp, membersResp, fundResp, anomResp, snapsResp, integrityResp] = await Promise.all([
        api.get(`/groups/${groupId}`),
        api.get(`/groups/${groupId}/reconcile/health`),
        api.get(`/groups/${groupId}/reconcile/members`, { params: periodParams }),
        api.get(`/groups/${groupId}/reconcile/fund-ledger`, { params: periodParams }),
        api.post(`/groups/${groupId}/reconcile/anomalies`, { ...periodParams, use_ai: false }),
        api.get(`/groups/${groupId}/reconcile/snapshots`),
        api.get(`/groups/${groupId}/integrity`).catch(() => ({ data: { latest: null } })),
      ]);
      setGroupName(groupResp.data.name);
      setHealth(healthResp.data);
      setMembers(membersResp.data.members || []);
      setFundLedger(fundResp.data);
      setAnomalies(anomResp.data);
      setSnapshots(snapsResp.data.snapshots || []);
      setIntegrityStatus(integrityResp.data.latest);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Failed to load reconciliation data');
    } finally {
      setLoading(false);
    }
  }, [groupId, periodParams]);

  // iter237 — acknowledge (ignore) the current drift: banner goes quiet until
  // the drift numbers actually change.
  const acknowledgeDrift = useCallback(async () => {
    if (ackBusy) return;
    setAckBusy(true);
    try {
      await api.post(`/groups/${groupId}/integrity/acknowledge`);
      const res = await api.get(`/groups/${groupId}/integrity`);
      setIntegrityStatus(res.data.latest);
      toast.success('Drift acknowledged — you\'ll only be alerted again if the numbers change');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to acknowledge drift');
    } finally {
      setAckBusy(false);
    }
  }, [groupId, ackBusy]);

  const handleRunIntegrity = useCallback(async () => {
    if (integrityBusy) return;
    setIntegrityBusy(true);
    try {
      const res = await api.post(`/groups/${groupId}/integrity/run`);
      setIntegrityStatus(res.data);
      if (res.data.ok) {
        toast.success('Integrity check passed — balances verified');
      } else {
        // iter147 — defensive formatting: drift_fund can be null/undefined if
        // the endpoint failed mid-flight. Show a meaningful message instead of
        // "off by ₹undefined".
        const drift = res.data.drift_fund;
        const coinDrifts = res.data.drift_coins || {};
        const coinCount = Object.keys(coinDrifts).length;
        if (drift != null && Number.isFinite(Number(drift))) {
          const msg = Math.abs(drift) > 0.01
            ? `Drift detected — fund off by ₹${Math.round(drift).toLocaleString('en-IN')}`
            : `Drift detected — ${coinCount} member${coinCount === 1 ? '' : 's'} have balance drift`;
          toast.error(msg);
        } else {
          toast.error('Integrity check returned an unexpected shape');
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Integrity check failed');
    } finally {
      setIntegrityBusy(false);
    }
  }, [groupId, integrityBusy]);

  /* iter149 — Auto-fix drift, two-step:
        1. previewAutoFix() calls the endpoint with ?dry_run=true and opens the
           confirmation modal with exact per-member deltas.
        2. applyAutoFix() commits and refetches the whole page. */
  const previewAutoFix = useCallback(async () => {
    if (autoFixBusy) return;
    if (!user?.is_super_admin) {
      toast.error('Super-admin only');
      return;
    }
    setAutoFixBusy(true);
    try {
      const res = await api.post(`/groups/${groupId}/integrity/auto-fix?dry_run=true`);
      setAutoFixPreview(res.data || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load preview');
    } finally {
      setAutoFixBusy(false);
    }
  }, [autoFixBusy, user, groupId]);

  const applyAutoFix = useCallback(async () => {
    if (autoFixBusy) return;
    setAutoFixBusy(true);
    try {
      const res = await api.post(`/groups/${groupId}/integrity/auto-fix`);
      const data = res.data || {};
      const fixedCoins = Array.isArray(data.coins_changed) ? data.coins_changed.length : 0;
      const fundDelta = Number(data.fund_delta || 0);
      if (Math.abs(fundDelta) < 0.01 && fixedCoins === 0) {
        toast.success('Already clean — no adjustments needed');
      } else {
        const fundLine = Math.abs(fundDelta) > 0.01
          ? `Fund ${fundDelta > 0 ? '+' : '\u2212'}\u20b9${Math.round(Math.abs(fundDelta)).toLocaleString('en-IN')}`
          : 'Fund unchanged';
        toast.success(`Drift cleared \u00b7 ${fundLine} \u00b7 ${fixedCoins} member${fixedCoins === 1 ? '' : 's'} fixed`);
      }
      if (data.post_check) setIntegrityStatus(data.post_check);
      setAutoFixPreview(null);
      setTraceOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Auto-fix failed');
    } finally {
      setAutoFixBusy(false);
    }
  }, [autoFixBusy, groupId, fetchAll]);

  /* iter151 — One-click download of the read-only ledger debug export. Super-
     admins only (server enforces this too). Downloads a timestamped JSON file
     containing every fund_transactions row + its per-row replay annotation. */
  const exportLedgerJson = useCallback(async () => {
    if (ledgerExportBusy) return;
    if (!user?.is_super_admin) {
      toast.error('Super-admin only');
      return;
    }
    setLedgerExportBusy(true);
    try {
      const res = await api.get(`/admin/group/${groupId}/ledger-export`);
      const data = res.data || {};
      const slug = (data.group?.name || 'group').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ledger-export-${slug}-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const drift = Number(data.drift?.fund || 0);
      const coinDrift = Object.keys(data.drift?.coins || {}).length;
      toast.success(`Exported ${data.row_count} row${data.row_count === 1 ? '' : 's'} · fund drift ${Math.abs(drift) < 0.01 ? 'clean' : `\u20b9${Math.round(Math.abs(drift)).toLocaleString('en-IN')}`} · ${coinDrift} member coin drift`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    } finally {
      setLedgerExportBusy(false);
    }
  }, [ledgerExportBusy, user, groupId]);

  /* iter152 — Reverse a single fund_transactions row. Super-admin only.
     Writes a `manual_reversal` sibling row + applies opposite delta. Used
     when one mis-booked entry is causing drift and Auto-fix is too blunt. */
  const handleReverseRow = useCallback(async () => {
    if (reverseBusy || !reverseTarget?.ref_id) return;
    if ((reverseReason || '').trim().length < 3) {
      toast.error('Reason must be at least 3 characters');
      return;
    }
    setReverseBusy(true);
    try {
      const res = await api.post(
        `/admin/fund-transactions/${reverseTarget.ref_id}/reverse`,
        { reason: reverseReason.trim() },
      );
      const data = res.data || {};
      const delta = Number(data.fund_delta_applied || 0);
      const fundLine = Math.abs(delta) > 0.01
        ? `Fund ${delta > 0 ? '+' : '\u2212'}\u20b9${Math.round(Math.abs(delta)).toLocaleString('en-IN')}`
        : 'No fund change';
      toast.success(`Row reversed \u00b7 ${fundLine}`);
      if (data.post_check) setIntegrityStatus(data.post_check);
      setReverseTarget(null);
      setReverseReason('');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reverse failed');
    } finally {
      setReverseBusy(false);
    }
  }, [reverseBusy, reverseTarget, reverseReason, fetchAll]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Hydrate ticks: read local immediately, then sync from server in the background
  useEffect(() => {
    if (!groupId || !periodKey) return;
    setTicks(readLocalTicks(groupId, periodKey));
    let cancelled = false;
    hydrateAndSync(groupId, periodKey).then(merged => {
      if (!cancelled) setTicks(new Set(merged));
    });
    return () => { cancelled = true; };
  }, [groupId, periodKey]);

  // Presence: heartbeat + SSE subscription so co-admins see each other
  useEffect(() => {
    if (!groupId || !periodKey) return;
    let cancelled = false;
    const sendHeartbeat = async () => {
      try {
        await api.post(`/groups/${groupId}/reconcile/presence/heartbeat`, { period_key: periodKey });
      } catch { /* ignore — best-effort */ }
    };
    const fetchPresence = async () => {
      try {
        const r = await api.get(`/groups/${groupId}/reconcile/presence`, { params: { period_key: periodKey } });
        if (!cancelled) setActiveReviewers(r.data.active || []);
      } catch { /* ignore */ }
    };
    const fetchChat = async () => {
      try {
        const r = await api.get(`/groups/${groupId}/reconcile/chat`, { params: { period_key: periodKey } });
        if (!cancelled) setChatMessages(r.data.messages || []);
      } catch { /* ignore */ }
    };
    const fetchAdmins = async () => {
      try {
        const r = await api.get(`/groups/${groupId}`);
        const admins = (r.data.members || []).filter(m => (r.data.admin_ids || []).includes(m.id));
        if (!cancelled) setAdminList(admins);
      } catch { /* ignore */ }
    };
    // Fire immediately + every 25s
    sendHeartbeat();
    fetchPresence();
    fetchChat();
    fetchAdmins();
    const hbInterval = setInterval(sendHeartbeat, 25000);
    const presInterval = setInterval(fetchPresence, 30000);

    // Subscribe to SSE updates
    const off = onEvent(['recon_presence_update', 'recon_tick_update', 'recon_chat_new', 'recon_chat_delete'], (type, payload) => {
      if (!payload || payload.group_id !== groupId || payload.period_key !== periodKey) return;
      if (type === 'recon_presence_update') {
        fetchPresence();
      } else if (type === 'recon_tick_update') {
        hydrateAndSync(groupId, periodKey).then(merged => {
          if (!cancelled) setTicks(new Set(merged));
        });
        toast.info(payload.message || 'Co-admin updated ticks', { duration: 2500 });
      } else if (type === 'recon_chat_new') {
        if (payload.message) {
          setChatMessages(prev => [...prev, payload.message]);
          toast.info(`💬 ${payload.message.author_name}: ${payload.message.body.slice(0, 80)}`, { duration: 4000 });
        }
      } else if (type === 'recon_chat_delete') {
        setChatMessages(prev => prev.filter(m => m.id !== payload.message_id));
      }
    });

    return () => {
      cancelled = true;
      clearInterval(hbInterval);
      clearInterval(presInterval);
      off();
    };
  }, [groupId, periodKey, onEvent]);

  // Tick handler — supports shift-click range selection on a given list of rowIds
  const handleTickRow = useCallback((rowId, isShift, visibleRowIds) => {
    let toToggle = [rowId];
    if (isShift && lastTickedRow && visibleRowIds && visibleRowIds.length) {
      const fromIdx = visibleRowIds.indexOf(lastTickedRow);
      const toIdx = visibleRowIds.indexOf(rowId);
      if (fromIdx !== -1 && toIdx !== -1) {
        const [a, b] = fromIdx < toIdx ? [fromIdx, toIdx] : [toIdx, fromIdx];
        toToggle = visibleRowIds.slice(a, b + 1);
      }
    }
    const action = ticks.has(rowId) ? 'untick' : 'tick';
    const newSet = toggleTicks(groupId, periodKey, toToggle, action);
    setTicks(new Set(newSet));
    setLastTickedRow(rowId);
  }, [groupId, periodKey, ticks, lastTickedRow]);

  const handleBulkTick = useCallback((rowIds, action) => {
    if (!rowIds.length) return;
    const newSet = toggleTicks(groupId, periodKey, rowIds, action);
    setTicks(new Set(newSet));
  }, [groupId, periodKey]);

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatBusy) return;
    setChatBusy(true);
    try {
      const r = await api.post(`/groups/${groupId}/reconcile/chat`, {
        period_key: periodKey,
        body: text,
      });
      // Optimistic: append our own message immediately (SSE doesn't broadcast back to self)
      setChatMessages(prev => [...prev, r.data]);
      setChatInput('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setChatBusy(false);
    }
  };

  const deleteChat = async (msgId) => {
    try {
      await api.delete(`/groups/${groupId}/reconcile/chat/${msgId}`);
      setChatMessages(prev => prev.filter(m => m.id !== msgId));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  // @mention autocomplete: detect '@' typing and surface admin name suggestions
  const mentionMatch = useMemo(() => {
    const m = chatInput.match(/@([A-Za-z][A-Za-z0-9 _.\-]*)$/);
    if (!m) return null;
    const q = m[1].toLowerCase();
    const matches = adminList.filter(a => a.name.toLowerCase().includes(q) && a.id !== user?.id).slice(0, 5);
    return matches.length ? { query: m[1], matches } : null;
  }, [chatInput, adminList, user]);

  const insertMention = (name) => {
    setChatInput(prev => prev.replace(/@([A-Za-z][A-Za-z0-9 _.\-]*)$/, `@${name} `));
  };

  const fetchMemberLedger = async (uid) => {
    try {
      setSelectedView(uid);
      setMemberLedger(null);
      const resp = await api.get(`/groups/${groupId}/reconcile/member/${uid}`, { params: periodParams });
      setMemberLedger(resp.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load member ledger');
    }
  };

  // iter271 — accept/undo a member's known drift so warnings quiet down until numbers change
  const acceptMemberDrift = async (uid) => {
    if (memberAckBusy) return;
    setMemberAckBusy(true);
    try {
      const r = await api.post(`/groups/${groupId}/reconcile/member/${uid}/ack-drift`);
      toast.success(r.data.message);
      const [membersResp, ledgerResp] = await Promise.all([
        api.get(`/groups/${groupId}/reconcile/members`, { params: periodParams }),
        api.get(`/groups/${groupId}/reconcile/member/${uid}`, { params: periodParams }),
      ]);
      setMembers(membersResp.data.members || []);
      setMemberLedger(ledgerResp.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to accept drift');
    } finally {
      setMemberAckBusy(false);
    }
  };

  const clearMemberDriftAck = async (uid) => {
    if (memberAckBusy) return;
    setMemberAckBusy(true);
    try {
      await api.delete(`/groups/${groupId}/reconcile/member/${uid}/ack-drift`);
      toast.success('Acceptance removed — drift will warn again');
      const [membersResp, ledgerResp] = await Promise.all([
        api.get(`/groups/${groupId}/reconcile/members`, { params: periodParams }),
        api.get(`/groups/${groupId}/reconcile/member/${uid}`, { params: periodParams }),
      ]);
      setMembers(membersResp.data.members || []);
      setMemberLedger(ledgerResp.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove acceptance');
    } finally {
      setMemberAckBusy(false);
    }
  };

  const acceptAllDrifts = async () => {
    if (ackAllBusy) return;
    setAckAllBusy(true);
    try {
      const r = await api.post(`/groups/${groupId}/reconcile/ack-all-drifts`);
      toast.success(r.data.message);
      const membersResp = await api.get(`/groups/${groupId}/reconcile/members`, { params: periodParams });
      setMembers(membersResp.data.members || []);
      if (selectedView !== 'fund' && selectedView) {
        const ledgerResp = await api.get(`/groups/${groupId}/reconcile/member/${selectedView}`, { params: periodParams });
        setMemberLedger(ledgerResp.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to accept drifts');
    } finally {
      setAckAllBusy(false);
    }
  };

  const runAiDeepScan = async () => {
    setAiLoading(true);
    try {
      const resp = await api.post(`/groups/${groupId}/reconcile/anomalies`, { ...periodParams, use_ai: true });
      setAnomalies(resp.data);
      const aiCount = resp.data.ai?.length || 0;
      if (resp.data.ai_error) {
        toast.error(`AI scan: ${resp.data.ai_error}`);
      } else {
        toast.success(`AI deep scan complete — ${aiCount} additional finding(s)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI scan failed');
    } finally {
      setAiLoading(false);
    }
  };

  const dismissAnomaly = async () => {
    if (!dismissTarget) return;
    if (dismissReason.trim().length < 3) {
      toast.error('Reason must be at least 3 characters');
      return;
    }
    try {
      await api.post(`/groups/${groupId}/reconcile/dismiss`, {
        kind: dismissTarget.kind,
        ref_id: dismissTarget.ref_id,
        reason: dismissReason.trim(),
      });
      toast.success('Anomaly dismissed');
      setDismissTarget(null);
      setDismissReason('');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dismiss failed');
    }
  };

  const markComplete = async () => {
    setCompleteBusy(true);
    try {
      const presetLabel = PERIOD_PRESETS.find(p => p.value === preset)?.label || preset;
      const resp = await api.post(`/groups/${groupId}/reconcile/complete`, {
        note: completeNote,
        ...periodParams,
        period_label: preset === 'custom' ? '' : presetLabel,  // server auto-generates if empty
      });
      toast.success(`Snapshot saved: ${resp.data.period_label} ✓`);
      setShowCompleteModal(false);
      setCompleteNote('');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark complete');
    } finally {
      setCompleteBusy(false);
    }
  };

  const openSnapshot = async (sid) => {
    setLoadingSnapshot(true);
    try {
      const resp = await api.get(`/groups/${groupId}/reconcile/snapshots/${sid}`);
      setSnapshotDetail(resp.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load snapshot');
    } finally {
      setLoadingSnapshot(false);
    }
  };

  const driftMembers = useMemo(() => members.filter(m => m.status === 'drift').length, [members]);
  const totalAnomalies = (anomalies.deterministic?.length || 0) + (anomalies.ai?.length || 0);

  return (
    <div className="space-y-4 max-w-7xl lg:max-w-none mx-auto" data-testid="reconcile-page">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <Link to={`/groups/${groupId}`} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="back-to-group-btn">
          <ArrowLeft className="w-4 h-4" /> back to group
        </Link>
        {/* Co-admin presence pills */}
        {activeReviewers.length > 0 && (
          <div className="flex items-center gap-1.5 ml-auto" data-testid="presence-pills">
            <span className="text-xs text-muted-foreground hidden sm:inline">Also reviewing:</span>
            {activeReviewers.slice(0, 3).map(r => (
              <span
                key={r.user_id}
                data-testid={`presence-${r.user_id}`}
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-xs"
                title={`${r.user_name} · last seen ${new Date(r.last_seen).toLocaleTimeString()}`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <span className="font-medium text-emerald-700">{r.user_name}</span>
              </span>
            ))}
            {activeReviewers.length > 3 && (
              <span className="text-xs text-muted-foreground">+{activeReviewers.length - 3}</span>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Reconciling</p>
          <h1 className="font-display text-3xl font-bold" data-testid="reconcile-group-name">{groupName || '...'}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Integrity pill — green when verified, red on drift, neutral when stale/never */}
          {(() => {
            const ok = integrityStatus?.ok === true;
            const fail = integrityStatus?.ok === false;
            const fmtAgo = (iso) => {
              if (!iso) return '';
              const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
              if (m === 0) return 'just now';
              if (m < 60) return `${m}m ago`;
              const h = Math.round(m / 60);
              if (h < 24) return `${h}h ago`;
              return `${Math.round(h / 24)}d ago`;
            };
            const cls = ok
              ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/15'
              : fail
              ? (integrityStatus?.acknowledged
                ? 'border-border bg-muted text-muted-foreground hover:bg-accent'
                : 'border-red-500/40 bg-red-500/10 text-red-700 hover:bg-red-500/15')
              : 'border-border bg-muted text-muted-foreground hover:bg-accent';
            return (
              <button
                type="button"
                onClick={handleRunIntegrity}
                disabled={integrityBusy}
                data-testid="integrity-pill"
                title={fail ? `Fund drift: ₹${integrityStatus.drift_fund} · click to re-run` : (ok ? `Last verified ${fmtAgo(integrityStatus.checked_at)} · click to re-run` : 'No integrity check yet · click to run')}
                className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full border-2 text-xs font-semibold transition-colors ${cls} ${integrityBusy ? 'opacity-60' : ''}`}
              >
                {ok ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldAlert className="w-3.5 h-3.5" />}
                <span data-testid="integrity-label">
                  {integrityBusy ? 'Checking…' : ok ? `Verified · ${fmtAgo(integrityStatus.checked_at)}` : fail ? (() => { const fd = Math.round(Math.abs(integrityStatus.drift_fund || 0)); const label = fd > 0 ? `Drift ₹${fd}` : `Drift · ${Object.keys(integrityStatus.drift_coins || {}).length} member${Object.keys(integrityStatus.drift_coins || {}).length === 1 ? '' : 's'}`; return integrityStatus?.acknowledged ? `${label} · ignored` : label; })() : 'Run integrity check'}
                </span>
              </button>
            );
          })()}
          <Select value={preset} onValueChange={setPreset}>
            <SelectTrigger className="w-44" data-testid="period-preset-select">
              <Calendar className="w-4 h-4 mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIOD_PRESETS.map(p => (
                <SelectItem key={p.value} value={p.value} data-testid={`preset-${p.value}`}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {preset === 'custom' && (
            <>
              <Input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="w-40" data-testid="custom-from-input" />
              <Input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="w-40" data-testid="custom-to-input" />
            </>
          )}
          <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          {user?.is_super_admin && (
            <Button
              variant="outline"
              size="sm"
              onClick={exportLedgerJson}
              disabled={ledgerExportBusy}
              title="Super-admin debug — export every fund_transactions row with the per-row replay annotation, used to diagnose unexplained drift."
              data-testid="export-ledger-btn"
            >
              {ledgerExportBusy
                ? (<><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Exporting…</>)
                : (<><Download className="w-4 h-4 mr-1" /> Export ledger JSON</>)}
            </Button>
          )}
          <Button onClick={() => setShowCompleteModal(true)} className="rounded-full" data-testid="mark-complete-btn">
            <FileCheck className="w-4 h-4 mr-1" /> Mark Complete
          </Button>
        </div>
      </div>

      {/* iter148 — Persistent inline drift banner. Renders only when a fail
           integrity result exists. "Trace each" expands a per-member breakdown
           with the top contributing transactions (those that look like
           personal-fronted reimburses, plus any custom fund flows).
           iter149 — Adds the super-admin "Auto-fix drift" action inside the
           expanded panel. */}
      {integrityStatus?.ok === false && (
        <DriftBanner
          status={integrityStatus}
          members={members}
          fundLedger={fundLedger}
          open={traceOpen}
          onToggle={() => setTraceOpen((v) => !v)}
          onJumpToMember={(uid) => { fetchMemberLedger(uid); setTraceOpen(false); window.scrollTo({ top: 200, behavior: 'smooth' }); }}
          canAutoFix={!!user?.is_super_admin}
          autoFixBusy={autoFixBusy}
          onAutoFix={previewAutoFix}
          onIgnore={acknowledgeDrift}
          ignoreBusy={ackBusy}
        />
      )}

      {/* iter154 — Total Group Funds hero card. The user-facing answer to
           "how much is the group worth right now?" — combines physical cash
           with net member dues. Sits above the technical Health Strip. */}
      {health && (
        <Card
          className="bg-gradient-to-br from-primary/10 via-emerald-500/5 to-background border-2 border-primary/30"
          data-testid="total-funds-card"
        >
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground">
                  Total Group Funds
                </p>
                <p
                  className="text-3xl sm:text-4xl font-display font-bold tabular-nums mt-0.5"
                  data-testid="total-group-funds-value"
                >
                  {formatINR(health.total_group_funds || 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1 leading-snug">
                  Cash <span className="font-semibold text-foreground tabular-nums">{formatINR(health.cash_in_hand || 0)}</span>
                  <span className="mx-1.5 opacity-50">+</span>
                  Net member dues <span className="font-semibold text-foreground tabular-nums">{formatINR(health.net_member_dues || 0)}</span>
                </p>
              </div>
              <div className="flex items-center gap-4 text-xs flex-wrap">
                <div className="text-right">
                  <p className="uppercase tracking-wider text-muted-foreground font-bold text-xs">Members owe</p>
                  <p className="font-semibold tabular-nums text-emerald-700">+{formatINR(health.members_owe_group_gross || 0)}</p>
                </div>
                {(health.group_owes_members_gross || 0) > 0.5 && (
                  <div className="text-right">
                    <p className="uppercase tracking-wider text-muted-foreground font-bold text-xs">Group owes</p>
                    <p className="font-semibold tabular-nums text-red-600">−{formatINR(health.group_owes_members_gross || 0)}</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Health Strip — iter155: removed FUND and NEXACOIN SUM chips (their
           info now lives in the Total Group Funds hero card above). Just the
           two operational signals remain: NeoPay mirror status + last
           reconciliation timestamp. */}
      {health && (
        <Card data-testid="health-strip">
          <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <HealthChip
              testid="health-neopay"
              icon={<Sparkles className="w-5 h-5" />}
              label="NeoPay"
              value={`${(health.neopay_total || 0) - (health.neopay_failed || 0)}/${health.neopay_total || 0}`}
              status={health.neopay_match}
              detail={health.neopay_match ? 'All transfers mirrored' : `${health.neopay_failed} mirror failure(s)`}
            />
            <HealthChip
              testid="health-last-recon"
              icon={<Clock className="w-5 h-5" />}
              label="Last Reconciled"
              value={health.last_reconciled_at
                ? new Date(health.last_reconciled_at).toLocaleDateString()
                : 'Never'}
              status={!!health.last_reconciled_at}
              detail={health.last_reconciled_at
                ? `by ${health.last_reconciled_by_name || 'admin'} · ${health.audit_count_since_last_recon} action(s) since`
                : 'Mark a reconciliation to start tracking'}
            />
          </CardContent>
        </Card>
      )}

      {/* Two-column: Members + Ledger */}
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        <Card className="self-start sticky top-20" data-testid="members-panel">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <UsersIcon className="w-4 h-4 text-primary" /> Members
            </CardTitle>
            {/* iter155 — Sidebar summary: show the net amount members owe the
                group (positive) or the group owes members (negative). Replaces
                the redundant "Group Fund" entry that previously sat here. */}
            {health && (
              <div className="mt-1.5 rounded-lg bg-primary/5 border border-primary/15 px-3 py-2" data-testid="members-total-outstanding">
                <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground">
                  Total Outstanding
                </p>
                <p className={`text-lg font-display font-bold tabular-nums ${
                  (health.net_member_dues || 0) >= 0 ? 'text-emerald-700' : 'text-red-600'
                }`}>
                  {(health.net_member_dues || 0) >= 0
                    ? `+${formatINR(health.net_member_dues || 0)}`
                    : `−${formatINR(Math.abs(health.net_member_dues || 0))}`}
                </p>
                <p className="text-xs text-muted-foreground leading-tight mt-0.5">
                  {(health.net_member_dues || 0) >= 0 ? 'members owe group (net)' : 'group owes members (net)'}
                </p>
              </div>
            )}
          </CardHeader>
          <CardContent className="p-0 max-h-[60vh] overflow-y-auto">
            {members.map(m => (
              <button
                key={m.user_id}
                onClick={() => fetchMemberLedger(m.user_id)}
                data-testid={`view-member-${m.user_id}`}
                className={`w-full text-left px-4 py-3 border-l-4 transition ${
                  selectedView === m.user_id
                    ? 'bg-primary/10 border-primary'
                    : 'border-transparent hover:bg-muted/50'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium truncate">{m.user_name}</span>
                  {m.status === 'match'
                    ? <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                    : m.acknowledged
                      ? <ShieldCheck className="w-4 h-4 text-slate-400 shrink-0" data-testid={`drift-ack-icon-${m.user_id}`} title={`Known drift · accepted by ${m.ack_by || 'admin'}`} />
                      : <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" data-testid={`drift-icon-${m.user_id}`} />}
                </div>
                <p className="text-xs text-muted-foreground font-mono mt-0.5">
                  {formatINRSigned(m.stored_balance)}
                  {m.status === 'drift' && !m.acknowledged && (
                    <span className="text-amber-600 ml-1">· off ₹{Math.round(Math.abs(m.balance_drift))}</span>
                  )}
                  {m.status === 'drift' && m.acknowledged && (
                    <span className="text-slate-400 ml-1" data-testid={`drift-known-label-${m.user_id}`}>· known ✓</span>
                  )}
                </p>
              </button>
            ))}
            {/* iter271 — accept all current drifts in one tap */}
            {members.some(m => m.status === 'drift' && !m.acknowledged) && (
              <div className="px-4 py-3 border-t">
                <button
                  onClick={acceptAllDrifts}
                  disabled={ackAllBusy}
                  data-testid="accept-all-drifts-btn"
                  className="w-full text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-full border-2 border-amber-500/40 text-amber-700 hover:bg-amber-500/10 transition disabled:opacity-50"
                >
                  {ackAllBusy ? 'Accepting…' : `Accept all drifts as known (${members.filter(m => m.status === 'drift' && !m.acknowledged).length})`}
                </button>
                <p className="text-xs text-muted-foreground mt-1.5 leading-snug">
                  Marks today's drifts as manually corrected. You'll be warned again only if a number changes.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="ledger-panel">
          <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
            <div className="flex items-center gap-2 min-w-0">
              {/* iter155 — When a member is selected, expose a quick switch
                  back to the Group Fund Ledger (which used to be the sidebar
                  entry that we removed). */}
              {selectedView !== 'fund' && (
                <button
                  onClick={() => { setSelectedView('fund'); setMemberLedger(null); }}
                  data-testid="back-to-fund-btn"
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                  title="Back to Group Fund Ledger"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Group Fund
                </button>
              )}
              <CardTitle className="text-base truncate">
                {selectedView === 'fund' ? 'Group Fund Ledger' : `${memberLedger?.user_name || ''} Ledger`}
              </CardTitle>
            </div>
            {(() => {
              const lg = selectedView === 'fund' ? fundLedger : memberLedger;
              if (!lg) return null;
              return (
                <span className="text-xs text-muted-foreground shrink-0">
                  {(lg.entries?.length || 0)} entries
                </span>
              );
            })()}
          </CardHeader>
          <CardContent className="p-0">
            <LedgerView
              ledger={selectedView === 'fund' ? fundLedger : memberLedger}
              loading={loading || (selectedView !== 'fund' && !memberLedger)}
              ticks={ticks}
              onTickRow={handleTickRow}
              onBulkTick={handleBulkTick}
              canReverse={!!user?.is_super_admin && selectedView === 'fund'}
              onReverseRow={(row) => { setReverseTarget(row); setReverseReason(''); }}
              onAcceptDrift={selectedView !== 'fund' ? () => acceptMemberDrift(selectedView) : null}
              onClearDriftAck={selectedView !== 'fund' ? () => clearMemberDriftAck(selectedView) : null}
              ackBusy={memberAckBusy}
            />
          </CardContent>
        </Card>
      </div>

      {/* Anomalies */}
      <Card data-testid="anomalies-card">
        <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-500" />
            Anomalies & Suggestions
            {totalAnomalies > 0 && (
              <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700 font-semibold">
                {totalAnomalies}
              </span>
            )}
          </CardTitle>
          <Button
            size="sm"
            variant={anomalies.ai_used ? 'outline' : 'default'}
            onClick={runAiDeepScan}
            disabled={aiLoading}
            data-testid="ai-deep-scan-btn"
          >
            <Sparkles className={`w-4 h-4 mr-1 ${aiLoading ? 'animate-pulse' : ''}`} />
            {aiLoading ? 'Scanning…' : anomalies.ai_used ? 'Re-run AI Scan' : 'Run Deep Scan with AI'}
          </Button>
        </CardHeader>
        <CardContent>
          {totalAnomalies === 0 ? (
            <div className="py-6 text-center text-muted-foreground" data-testid="no-anomalies">
              <CheckCircle className="w-8 h-8 mx-auto text-emerald-500 mb-2" />
              No anomalies detected in this period
            </div>
          ) : (
            <div className="space-y-3">
              {[...anomalies.deterministic, ...anomalies.ai].map((a, i) => (
                <AnomalyCard
                  key={`${a.kind}-${a.ref_id || i}`}
                  anomaly={a}
                  onDismiss={() => setDismissTarget(a)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Past Snapshots */}
      <Card data-testid="snapshots-card">
        <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Archive className="w-4 h-4 text-primary" />
            Reconciliation Snapshots
            {snapshots.length > 0 && (
              <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-semibold">
                {snapshots.length}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {snapshots.length === 0 ? (
            <p className="text-sm text-muted-foreground py-3" data-testid="no-snapshots">
              No snapshots yet. Click <b>Mark Complete</b> after reviewing a period to save an immutable snapshot —
              useful for tax-time review and onboarding new admins.
            </p>
          ) : (
            <div className="grid gap-2">
              {snapshots.map(s => (
                <button
                  key={s.id}
                  onClick={() => openSnapshot(s.id)}
                  data-testid={`snapshot-${s.id}`}
                  className="w-full text-left p-3 rounded-xl border hover:border-primary hover:bg-primary/5 transition flex items-center gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{s.period_label}</span>
                      {s.anomaly_count > 0 && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-700 font-semibold">
                          {s.anomaly_count} anomal{s.anomaly_count === 1 ? 'y' : 'ies'}
                        </span>
                      )}
                      {Math.abs(s.fund_drift || 0) >= 0.01 && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-600 font-semibold">
                          Drift ₹{Math.round(Math.abs(s.fund_drift)).toLocaleString('en-IN')}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Closed at {formatINR(s.fund_closing)} · {new Date(s.created_at).toLocaleDateString()} ·
                      by {s.created_by_name}
                      {s.note && ` · "${s.note.slice(0, 60)}${s.note.length > 60 ? '…' : ''}"`}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reconciliation Chat (per-period thread) */}
      <Card data-testid="chat-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-primary" />
            Period Chat
            {chatMessages.length > 0 && (
              <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-semibold">
                {chatMessages.length}
              </span>
            )}
            <span className="ml-auto text-xs text-muted-foreground hidden sm:flex items-center gap-1">
              <AtSign className="w-3 h-3" /> mention admins
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Messages */}
          <div className="space-y-2 max-h-72 overflow-y-auto mb-3 pr-1" data-testid="chat-messages">
            {chatMessages.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center" data-testid="chat-empty">
                No messages yet. Leave a note for co-admins or @mention someone to get their attention.
              </p>
            ) : (
              chatMessages.map(m => {
                const isMine = m.author_id === user?.id;
                const mentioned = (m.mentions || []).includes(user?.id);
                return (
                  <div
                    key={m.id}
                    data-testid={`chat-msg-${m.id}`}
                    className={`p-2 rounded-xl border-l-2 ${
                      mentioned ? 'bg-amber-500/5 border-l-amber-500'
                      : isMine ? 'bg-primary/5 border-l-primary'
                      : 'bg-muted/30 border-l-muted-foreground/30'
                    }`}
                  >
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{m.author_name}</span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(m.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {mentioned && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-700 font-semibold">
                          mentions you
                        </span>
                      )}
                      {isMine && (
                        <button
                          onClick={() => deleteChat(m.id)}
                          className="ml-auto opacity-50 hover:opacity-100 hover:text-red-500 transition"
                          data-testid={`chat-delete-${m.id}`}
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <ChatBody body={m.body} />
                  </div>
                );
              })
            )}
          </div>

          {/* Input + send */}
          <div className="relative">
            <Textarea
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  sendChat();
                }
              }}
              placeholder="Leave a note… use @ to mention admins. Cmd/Ctrl+Enter to send."
              className="min-h-[68px] resize-none"
              data-testid="chat-input"
              maxLength={1000}
            />
            {/* @mention autocomplete */}
            {mentionMatch && (
              <div className="absolute bottom-full left-0 mb-1 bg-card border-2 rounded-xl shadow-lg p-1 z-10 min-w-[180px]" data-testid="mention-autocomplete">
                {mentionMatch.matches.map(a => (
                  <button
                    key={a.id}
                    onClick={() => insertMention(a.name)}
                    data-testid={`mention-suggestion-${a.id}`}
                    className="w-full text-left px-3 py-1.5 rounded-lg hover:bg-primary/10 text-sm flex items-center gap-2"
                  >
                    <AtSign className="w-3.5 h-3.5 text-primary" />
                    {a.name}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-muted-foreground">
                {chatInput.length}/1000
              </span>
              <Button
                size="sm"
                onClick={sendChat}
                disabled={!chatInput.trim() || chatBusy}
                data-testid="chat-send-btn"
              >
                <Send className="w-4 h-4 mr-1" />
                {chatBusy ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* iter152 — Single-row reverse confirmation (super-admin) */}
      <Dialog open={!!reverseTarget} onOpenChange={(open) => { if (!open && !reverseBusy) { setReverseTarget(null); setReverseReason(''); } }}>
        <DialogContent className="max-w-md" data-testid="reverse-row-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Undo2 className="w-5 h-5 text-red-600" /> Reverse ledger row
            </DialogTitle>
          </DialogHeader>
          {reverseTarget && (
            <div className="space-y-3 text-sm">
              <div className="rounded-lg border bg-background p-3">
                <p className="font-semibold truncate">{reverseTarget.description || '—'}</p>
                <p className="text-xs text-muted-foreground mt-0.5 tabular-nums">
                  {new Date(reverseTarget.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  {' · '}
                  {(reverseTarget.dr || 0) > 0 ? `Dr ${formatINR(reverseTarget.dr)}` : `Cr ${formatINR(reverseTarget.cr)}`}
                </p>
              </div>
              <p className="text-muted-foreground leading-relaxed text-[12px]">
                Writes a compensating <code className="font-mono">manual_reversal</code> row and updates stored balances. The original row stays in the ledger (struck through) so the history is preserved. Audit-logged.
              </p>
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Reason (required, audit-logged)</label>
                <Textarea
                  value={reverseReason}
                  onChange={(e) => setReverseReason(e.target.value)}
                  placeholder="e.g. duplicate session-fee entry on 14-May"
                  rows={2}
                  className="mt-1 text-sm"
                  data-testid="reverse-row-reason"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReverseTarget(null); setReverseReason(''); }} disabled={reverseBusy} data-testid="reverse-row-cancel-btn">Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleReverseRow}
              disabled={reverseBusy || (reverseReason || '').trim().length < 3}
              data-testid="reverse-row-confirm-btn"
            >
              {reverseBusy
                ? (<><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Reversing…</>)
                : (<><Undo2 className="w-4 h-4 mr-1" /> Reverse row</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter149 — Auto-fix preview modal (super-admin) */}
      <Dialog open={!!autoFixPreview} onOpenChange={(open) => { if (!open && !autoFixBusy) setAutoFixPreview(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="auto-fix-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Wrench className="w-5 h-5 text-red-600" /> Auto-fix drift — preview
            </DialogTitle>
          </DialogHeader>
          {autoFixPreview && (
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground leading-relaxed">
                Review the exact changes below before applying. Stored balances will be snapped to the immutable <code className="font-mono">fund_transactions</code> ledger. The ledger itself is NOT touched and this action is logged to audit.
              </p>

              {/* Fund row */}
              <div className="rounded-lg border bg-background p-3 flex items-center justify-between gap-3" data-testid="auto-fix-fund-row">
                <div className="min-w-0">
                  <p className="font-semibold">Group Fund</p>
                  <p className="text-xs text-muted-foreground tabular-nums">
                    {formatINRSigned(autoFixPreview.fund_before)} → <span className="font-semibold text-foreground">{formatINRSigned(autoFixPreview.fund_after)}</span>
                  </p>
                </div>
                <span className={`text-sm font-bold tabular-nums shrink-0 ${Math.abs(Number(autoFixPreview.fund_delta) || 0) < 0.01 ? 'text-muted-foreground' : (Number(autoFixPreview.fund_delta) > 0 ? 'text-emerald-600' : 'text-red-600')}`}>
                  {Math.abs(Number(autoFixPreview.fund_delta) || 0) < 0.01 ? 'no change' : formatINRSigned(autoFixPreview.fund_delta)}
                </span>
              </div>

              {/* Members */}
              {(autoFixPreview.coins_changed?.length || 0) > 0 ? (
                <div>
                  <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-1.5">
                    Members ({autoFixPreview.coins_changed.length})
                  </p>
                  <ul className="space-y-1 max-h-[40vh] overflow-y-auto pr-1">
                    {autoFixPreview.coins_changed.map((c) => (
                      <li key={c.user_id} className="rounded-lg border bg-background p-2.5 flex items-center justify-between gap-3" data-testid={`auto-fix-member-${c.user_id}`}>
                        <div className="min-w-0">
                          <p className="font-medium truncate">{c.user_name || c.user_id}</p>
                          <p className="text-xs text-muted-foreground tabular-nums">
                            {formatINRSigned(c.before)} → <span className="font-semibold text-foreground">{formatINRSigned(c.after)}</span>
                          </p>
                        </div>
                        <span className={`text-sm font-bold tabular-nums shrink-0 ${Number(c.delta) > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {formatINRSigned(c.delta)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic">No member ₹ adjustments needed.</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAutoFixPreview(null)} disabled={autoFixBusy} data-testid="auto-fix-cancel-btn">Cancel</Button>
            <Button
              variant="destructive"
              onClick={applyAutoFix}
              disabled={autoFixBusy}
              data-testid="auto-fix-apply-btn"
            >
              {autoFixBusy
                ? (<><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Applying…</>)
                : (<><Wrench className="w-4 h-4 mr-1" /> Apply fix</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Complete Modal */}
      <Dialog open={showCompleteModal} onOpenChange={setShowCompleteModal}>
        <DialogContent data-testid="complete-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-primary" /> Mark Reconciliation Complete
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This stamps {groupName} as reconciled at this moment. Next time you open this page,
            only entries created after this will count as "new". Optionally add a note for the audit log.
          </p>
          <Textarea
            placeholder="e.g. Reconciled May 2026. Verified all entries against bank statement."
            value={completeNote}
            onChange={e => setCompleteNote(e.target.value)}
            data-testid="complete-note-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompleteModal(false)} data-testid="complete-cancel-btn">Cancel</Button>
            <Button onClick={markComplete} disabled={completeBusy} data-testid="complete-confirm-btn">
              {completeBusy ? 'Saving…' : 'Mark Complete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dismiss Anomaly Modal */}
      <Dialog open={!!dismissTarget} onOpenChange={(open) => { if (!open) { setDismissTarget(null); setDismissReason(''); } }}>
        <DialogContent data-testid="dismiss-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Dismiss Anomaly</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{dismissTarget?.title}</p>
          <Textarea
            placeholder="Reason (audit-logged) — e.g. 'Confirmed with member, fine is correct'"
            value={dismissReason}
            onChange={e => setDismissReason(e.target.value)}
            data-testid="dismiss-reason-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDismissTarget(null); setDismissReason(''); }}>Cancel</Button>
            <Button onClick={dismissAnomaly} data-testid="dismiss-confirm-btn">Dismiss</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Snapshot Detail Dialog */}
      <Dialog open={!!snapshotDetail} onOpenChange={(open) => { if (!open) setSnapshotDetail(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="snapshot-detail-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Archive className="w-5 h-5 text-primary" />
              {snapshotDetail?.period_label || 'Snapshot'}
            </DialogTitle>
          </DialogHeader>
          {snapshotDetail && (
            <div className="space-y-4">
              <div className="text-xs text-muted-foreground">
                Closed at {new Date(snapshotDetail.created_at).toLocaleString()} by{' '}
                <b>{snapshotDetail.created_by_name}</b>
                {snapshotDetail.note && (
                  <p className="mt-1 italic">"{snapshotDetail.note}"</p>
                )}
              </div>

              {/* Fund summary */}
              <div className="border-2 rounded-xl p-3" data-testid="snapshot-fund-summary">
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2 flex items-center gap-1">
                  <Wallet className="w-3.5 h-3.5" /> Group Fund
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 font-mono text-xs">
                  <div>
                    <p className="text-muted-foreground uppercase">Opening</p>
                    <p className="font-semibold">{formatINRSigned(snapshotDetail.fund.opening_balance)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground uppercase">Dr</p>
                    <p className="font-semibold text-red-600">{formatINR(snapshotDetail.fund.total_dr)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground uppercase">Cr</p>
                    <p className="font-semibold text-emerald-600">{formatINR(snapshotDetail.fund.total_cr)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground uppercase">Closing</p>
                    <p className="font-semibold">{formatINRSigned(snapshotDetail.fund.closing_balance)}</p>
                  </div>
                </div>
                {Math.abs(snapshotDetail.fund.balance_drift || 0) >= 0.01 && (
                  <p className="mt-2 text-xs text-amber-700">
                    ⚠ Drift ₹{Math.round(Math.abs(snapshotDetail.fund.balance_drift)).toLocaleString('en-IN')} (stored vs computed)
                  </p>
                )}
              </div>

              {/* Members table */}
              <div className="border rounded-xl overflow-hidden">
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold p-3 pb-2 flex items-center gap-1">
                  <UsersIcon className="w-3.5 h-3.5" /> Members ({snapshotDetail.members.length})
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Member</th>
                        <th className="text-right px-3 py-2 font-medium">Opening</th>
                        <th className="text-right px-3 py-2 font-medium">Dr</th>
                        <th className="text-right px-3 py-2 font-medium">Cr</th>
                        <th className="text-right px-3 py-2 font-medium">Closing</th>
                      </tr>
                    </thead>
                    <tbody>
                      {snapshotDetail.members.map(m => (
                        <tr key={m.user_id} className="border-t" data-testid={`snapshot-member-${m.user_id}`}>
                          <td className="px-3 py-1.5">
                            <span className="font-medium">{m.user_name}</span>
                            {Math.abs(m.balance_drift || 0) >= 0.01 && (
                              <span className="ml-1 text-xs px-1 py-0.5 rounded-full bg-amber-500/15 text-amber-700 font-semibold">⚠</span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-right font-mono">{formatINRSigned(m.opening_balance)}</td>
                          <td className="px-3 py-1.5 text-right font-mono text-red-600">{m.total_dr > 0 ? formatINR(m.total_dr) : '—'}</td>
                          <td className="px-3 py-1.5 text-right font-mono text-emerald-600">{m.total_cr > 0 ? formatINR(m.total_cr) : '—'}</td>
                          <td className="px-3 py-1.5 text-right font-mono font-semibold">{formatINRSigned(m.closing_balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="text-xs text-muted-foreground italic flex items-center gap-1">
                <FileCheck className="w-3 h-3" /> This snapshot is immutable — frozen at the moment of "Mark Complete".
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HealthChip({ icon, label, value, status, detail, testid }) {
  const ok = status === true;
  const tone = ok
    ? 'border-emerald-500/30 bg-emerald-500/5'
    : 'border-amber-500/30 bg-amber-500/5';
  const Icon = ok ? CheckCircle : AlertTriangle;
  const iconCol = ok ? 'text-emerald-500' : 'text-amber-500';
  return (
    <div className={`p-3 rounded-xl border-2 ${tone}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
        {icon}
        <span>{label}</span>
        <Icon className={`w-3.5 h-3.5 ml-auto ${iconCol}`} />
      </div>
      <p className="font-display text-xl font-bold mt-1">{value}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{detail}</p>
    </div>
  );
}

function LedgerView({ ledger, loading, ticks, onTickRow, onBulkTick, canReverse, onReverseRow, onAcceptDrift, onClearDriftAck, ackBusy }) {
  if (loading || !ledger) {
    return (
      <div className="p-6 space-y-2">
        <div className="skeleton h-4 w-1/3 rounded" />
        <div className="skeleton h-4 w-2/3 rounded" />
        <div className="skeleton h-4 w-1/2 rounded" />
      </div>
    );
  }
  if ((ledger.entries || []).length === 0) {
    return <div className="p-6 text-center text-muted-foreground">No entries in this period</div>;
  }

  // Group entries by day for daily-totals rendering
  const dayMap = {};
  (ledger.entries || []).forEach(e => {
    const day = (e.date || '').slice(0, 10);
    if (!dayMap[day]) dayMap[day] = [];
    dayMap[day].push(e);
  });
  const days = Object.keys(dayMap).sort();
  const dailyTotMap = Object.fromEntries((ledger.daily_totals || []).map(d => [d.day, d]));

  // Tick stats
  const visibleRowIds = (ledger.entries || []).map(e => e.ref_id).filter(Boolean);
  const tickedVisible = visibleRowIds.filter(rid => ticks?.has(rid));
  const tickedCount = tickedVisible.length;
  const totalCount = visibleRowIds.length;
  const tickedAmt = (ledger.entries || [])
    .filter(e => ticks?.has(e.ref_id))
    .reduce((s, e) => s + (e.dr || 0) + (e.cr || 0), 0);
  const totalAmt = ledger.total_dr + ledger.total_cr;
  const allTicked = tickedCount > 0 && tickedCount === totalCount;
  const noneTicked = tickedCount === 0;

  return (
    <div className="overflow-x-auto">
      {/* Tick progress strip */}
      <div className="px-4 py-2 bg-primary/5 border-b flex flex-wrap items-center gap-3 justify-between" data-testid="tick-progress-strip">
        <div className="flex items-center gap-3 flex-wrap text-xs">
          <span className="font-mono">
            <span className="font-bold text-primary">{tickedCount}</span> / {totalCount} verified
          </span>
          <span className="text-muted-foreground">·</span>
          <span className="font-mono text-muted-foreground">
            ₹{Math.round(tickedAmt).toLocaleString('en-IN')} of ₹{Math.round(totalAmt).toLocaleString('en-IN')}
          </span>
          <div className="hidden sm:block w-32 h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${totalCount ? (tickedCount / totalCount) * 100 : 0}%` }}
            />
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onBulkTick(visibleRowIds, 'tick')}
            disabled={allTicked || !onBulkTick}
            data-testid="tick-all-btn"
            className="h-7 text-xs"
          >
            <CheckSquare className="w-3.5 h-3.5 mr-1" /> Tick all
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onBulkTick(visibleRowIds, 'untick')}
            disabled={noneTicked || !onBulkTick}
            data-testid="untick-all-btn"
            className="h-7 text-xs"
          >
            <Square className="w-3.5 h-3.5 mr-1" /> Untick all
          </Button>
        </div>
      </div>

      {/* Opening row */}
      <div className="px-4 py-2 bg-muted/40 border-b font-mono text-xs flex justify-between" data-testid="opening-row">
        <span className="font-semibold uppercase tracking-wider text-muted-foreground">Opening Balance</span>
        <span className="font-semibold">{formatINRSigned(ledger.opening_balance)}</span>
      </div>

      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wider text-muted-foreground">
          <tr className="border-b">
            <th className="px-2 py-2 font-medium w-8" title="Tick to mark verified"></th>
            <th className="text-left px-3 py-2 font-medium">Date</th>
            <th className="text-left px-3 py-2 font-medium">Description</th>
            <th className="text-right px-3 py-2 font-medium">Dr</th>
            <th className="text-right px-3 py-2 font-medium">Cr</th>
            <th className="text-right px-3 py-2 font-medium">Balance</th>
            {canReverse && <th className="px-2 py-2 font-medium w-10" title="Super-admin row actions"></th>}
          </tr>
        </thead>
        <tbody>
          {days.map(day => (
            <>
              {dayMap[day].map((e, idx) => {
                const isTicked = ticks?.has(e.ref_id);
                return (
                  <tr
                    key={`${e.ref_id || day}-${idx}`}
                    data-testid={`ledger-row-${e.ref_id}`}
                    className={`border-b hover:bg-muted/30 ${e.reversed ? 'opacity-50' : ''} ${isTicked ? 'bg-emerald-500/5 border-l-2 border-l-emerald-500' : ''}`}
                  >
                    <td className="px-2 py-2 text-center">
                      {e.ref_id && onTickRow ? (
                        <button
                          onClick={(ev) => onTickRow(e.ref_id, ev.shiftKey, visibleRowIds)}
                          data-testid={`tick-${e.ref_id}`}
                          title={isTicked ? 'Untick (Shift-click for range)' : 'Tick (Shift-click for range)'}
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition ${
                            isTicked
                              ? 'bg-emerald-500 border-emerald-500 text-white'
                              : 'border-muted-foreground/40 hover:border-primary'
                          }`}
                        >
                          {isTicked && <CheckCircle className="w-3 h-3" strokeWidth={3} />}
                        </button>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                      {new Date(e.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={e.reversed ? 'line-through' : ''}>{e.description || '—'}</span>
                        {e.fine_type && (
                          <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-700 font-semibold uppercase tracking-wide">
                            {e.fine_type.replace(/_/g, ' ')}
                          </span>
                        )}
                        {e.user_name && (
                          <span className="text-xs text-muted-foreground">· {e.user_name}</span>
                        )}
                        {e.neopay_status === 'submitted' && (
                          <span className="text-xs text-emerald-600" title="NeoPay mirrored">✓ NP</span>
                        )}
                        {e.neopay_status === 'submit_failed' && (
                          <span className="text-xs text-red-600" title="NeoPay mirror failed">✗ NP</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-red-600">
                      {e.dr > 0 ? formatINR(e.dr) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-600">
                      {e.cr > 0 ? formatINR(e.cr) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">
                      {formatINRSigned(e.balance)}
                    </td>
                    {canReverse && (
                      <td className="px-2 py-2 text-center">
                        {e.ref_id && !e.reversed ? (
                          <button
                            onClick={() => onReverseRow?.(e)}
                            data-testid={`reverse-row-${e.ref_id}`}
                            title="Super-admin: reverse this row (writes a compensating manual_reversal entry)"
                            className="w-6 h-6 rounded text-muted-foreground hover:text-red-600 hover:bg-red-500/10 flex items-center justify-center transition"
                          >
                            <Undo2 className="w-3.5 h-3.5" />
                          </button>
                        ) : e.reversed ? (
                          <span className="text-xs text-muted-foreground italic" title="Already reversed">rev</span>
                        ) : null}
                      </td>
                    )}
                  </tr>
                );
              })}
              {dailyTotMap[day] && (
                <tr className="bg-muted/30 border-b text-xs font-semibold" data-testid={`daily-total-${day}`}>
                  <td></td>
                  <td className="px-3 py-1.5 font-mono uppercase tracking-wider">{day} Total</td>
                  <td className="px-3 py-1.5"></td>
                  <td className="px-3 py-1.5 text-right font-mono text-red-600">{formatINR(dailyTotMap[day].dr)}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-emerald-600">{formatINR(dailyTotMap[day].cr)}</td>
                  <td className="px-3 py-1.5 text-right font-mono">
                    {formatINRSigned(dailyTotMap[day].end_balance)}
                  </td>
                  {canReverse && <td></td>}
                </tr>
              )}
            </>
          ))}
        </tbody>
      </table>

      {/* Footer summary */}
      <div className="px-4 py-3 bg-muted/40 border-t font-mono text-xs grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="ledger-summary">
        <div>
          <p className="text-muted-foreground uppercase tracking-wider">Opening</p>
          <p className="font-semibold">{formatINRSigned(ledger.opening_balance)}</p>
        </div>
        <div>
          <p className="text-muted-foreground uppercase tracking-wider">Total Dr</p>
          <p className="font-semibold text-red-600">{formatINR(ledger.total_dr)}</p>
        </div>
        <div>
          <p className="text-muted-foreground uppercase tracking-wider">Total Cr</p>
          <p className="font-semibold text-emerald-600">{formatINR(ledger.total_cr)}</p>
        </div>
        <div>
          <p className="text-muted-foreground uppercase tracking-wider">Closing</p>
          <p className="font-semibold">{formatINRSigned(ledger.closing_balance)}</p>
        </div>
      </div>
      {Math.abs(ledger.balance_drift || 0) >= 0.01 && !ledger.acknowledged && (
        <div className="px-4 py-2 bg-amber-500/10 border-t border-amber-500/30 text-xs text-amber-800" data-testid="drift-banner">
          ⚠ Stored balance ({formatINRSigned(ledger.stored_balance)}) disagrees with computed
          ({formatINRSigned(ledger.computed_balance)}) by {formatINRSigned(ledger.balance_drift)}.
          Likely cause: legacy seed data set balance directly without writing fund_transactions.
          {onAcceptDrift && (
            <button
              onClick={onAcceptDrift}
              disabled={ackBusy}
              data-testid="accept-drift-btn"
              className="ml-2 px-2.5 py-1 rounded-full border border-amber-600/50 font-bold uppercase tracking-wider text-xs hover:bg-amber-500/20 transition disabled:opacity-50"
            >
              {ackBusy ? 'Accepting…' : '✓ Accept as corrected'}
            </button>
          )}
        </div>
      )}
      {Math.abs(ledger.balance_drift || 0) >= 0.01 && ledger.acknowledged && (
        <div className="px-4 py-2 bg-slate-500/5 border-t border-slate-300/40 text-xs text-slate-500" data-testid="drift-ack-banner">
          ✓ Known drift of {formatINRSigned(ledger.balance_drift)} — accepted by {ledger.ack_by || 'admin'}
          {ledger.ack_at ? ` on ${new Date(ledger.ack_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}` : ''}.
          You'll be warned again only if this number changes.
          {onClearDriftAck && (
            <button
              onClick={onClearDriftAck}
              disabled={ackBusy}
              data-testid="undo-drift-ack-btn"
              className="ml-2 px-2 py-0.5 rounded-full border border-slate-400/50 text-xs font-bold uppercase tracking-wider hover:bg-slate-500/10 transition disabled:opacity-50"
            >
              Undo
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ChatBody({ body }) {
  // Render @mentions as styled chips inline
  const parts = (body || '').split(/(@[A-Za-z][A-Za-z0-9 _.\-]*)/);
  return (
    <p className="text-sm mt-1 whitespace-pre-wrap break-words">
      {parts.map((p, i) =>
        p.startsWith('@') ? (
          <span key={i} className="text-primary font-semibold bg-primary/10 px-1 rounded">{p}</span>
        ) : <span key={i}>{p}</span>
      )}
    </p>
  );
}

function AnomalyCard({ anomaly, onDismiss }) {
  const sevTone = {
    high: 'border-red-500/40 bg-red-500/5',
    medium: 'border-amber-500/40 bg-amber-500/5',
    low: 'border-blue-500/40 bg-blue-500/5',
  }[anomaly.severity] || 'border-muted bg-muted/30';
  const sevIcon = {
    high: <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />,
    medium: <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />,
    low: <Eye className="w-4 h-4 text-blue-500 shrink-0" />,
  }[anomaly.severity] || <AlertCircle className="w-4 h-4 shrink-0" />;
  const isAi = anomaly.kind === 'ai_anomaly';

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-3 rounded-xl border-2 ${sevTone} flex gap-3 items-start`}
      data-testid={`anomaly-${anomaly.kind}-${anomaly.ref_id || 'multi'}`}
    >
      {sevIcon}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-semibold text-sm">{anomaly.title}</p>
          {isAi && (
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-primary/15 text-primary font-semibold uppercase tracking-wide flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> AI
            </span>
          )}
          <span className={`text-xs px-1.5 py-0.5 rounded-full uppercase tracking-wide font-semibold ${
            anomaly.severity === 'high' ? 'bg-red-500/15 text-red-600' :
            anomaly.severity === 'medium' ? 'bg-amber-500/15 text-amber-700' :
            'bg-blue-500/15 text-blue-700'
          }`}>{anomaly.severity}</span>
        </div>
        <p className="text-sm text-muted-foreground mt-1">{anomaly.description}</p>
      </div>
      {anomaly.dismissable && (
        <Button
          size="sm"
          variant="ghost"
          onClick={onDismiss}
          data-testid={`dismiss-${anomaly.kind}-${anomaly.ref_id || 'multi'}`}
          title="Dismiss"
        >
          <X className="w-4 h-4" />
        </Button>
      )}
    </motion.div>
  );
}

/* ================================================================== */
/* iter148 — DriftBanner: persistent inline alert + trace breakdown    */
/* ================================================================== */
function DriftBanner({ status, members, fundLedger, open, onToggle, onJumpToMember, canAutoFix, autoFixBusy, onAutoFix, onIgnore, ignoreBusy }) {
  const fundDrift = Number(status?.drift_fund || 0);
  const coinDrifts = status?.drift_coins || {};
  const acknowledged = status?.acknowledged === true;

  // Build a member→drift list, sorted by absolute drift desc.
  const driftedMembers = useMemo(() => {
    const list = [];
    for (const m of (members || [])) {
      const d = Number(coinDrifts[m.user_id] ?? m.balance_drift ?? 0);
      if (Math.abs(d) > 0.5) list.push({ ...m, drift: d });
    }
    list.sort((a, b) => Math.abs(b.drift) - Math.abs(a.drift));
    return list;
  }, [members, coinDrifts]);

  // Pre-compute the count of `is_refundable_personal` rows in the fund ledger,
  // which are the most common drift contributors after iter147 deploys.
  const refundRowCount = useMemo(() => {
    if (!fundLedger?.entries) return 0;
    return fundLedger.entries.filter((e) =>
      (e.note || '').startsWith('Refunded to ') ||
      e.reimburse_from_fund === true
    ).length;
  }, [fundLedger]);

  const fmt = (n) => `₹${Math.round(Math.abs(n)).toLocaleString('en-IN')}`;

  // iter237 — acknowledged drift renders as a quiet one-liner instead of the
  // red alarm. Expanding it still shows the full trace panel.
  if (acknowledged && !open) {
    return (
      <div
        className="rounded-2xl border border-border bg-muted/40 px-4 py-2.5 flex items-center gap-3"
        data-testid="drift-banner-acknowledged"
      >
        <ShieldAlert className="w-4 h-4 text-muted-foreground shrink-0" />
        <p className="text-xs text-muted-foreground flex-1 min-w-0 truncate">
          {Math.abs(fundDrift) > 0.01 ? `Drift ${fmt(fundDrift)}` : 'Balance drift'} acknowledged by <span className="font-semibold">{status?.acknowledged_by}</span> — you'll be alerted if the numbers change.
        </p>
        <button
          type="button"
          onClick={onToggle}
          data-testid="drift-banner-show-details"
          className="text-xs font-bold text-primary shrink-0 hover:underline"
        >
          Details
        </button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border-2 border-red-500/40 bg-red-500/5 overflow-hidden"
      data-testid="drift-banner"
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid="drift-banner-toggle"
        className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-red-500/10 transition-colors"
      >
        <ShieldAlert className="w-5 h-5 text-red-600 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm text-red-700">
            ⚠ Fund drift: {fmt(fundDrift)}
            {driftedMembers.length > 0 && (
              <> · balance drift across <span className="tabular-nums">{driftedMembers.length}</span> member{driftedMembers.length === 1 ? '' : 's'}</>
            )}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {open ? 'Hide details' : 'Trace each contributor'} · last checked {status?.checked_at ? new Date(status.checked_at).toLocaleString() : 'unknown'}
          </p>
        </div>
        {!acknowledged && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); if (!ignoreBusy) onIgnore?.(); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onIgnore?.(); } }}
            data-testid="drift-banner-ignore-btn"
            className="shrink-0 px-3 py-1 rounded-full border border-red-500/40 text-xs font-bold text-red-700 hover:bg-red-500/10 transition-colors"
            title="Mark this drift as known — hides the alert until the numbers change"
          >
            {ignoreBusy ? 'Ignoring…' : 'Ignore'}
          </span>
        )}
        {open
          ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
          : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-red-500/20" data-testid="drift-trace-panel">
          {/* iter149 — Auto-fix action: super-admin only. Recomputes stored
              balances from the immutable ledger in a single click. */}
          {canAutoFix && (
            <div className="pt-3">
              <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 flex items-start gap-3">
                <Wrench className="w-4 h-4 text-red-700 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-red-700">Auto-fix drift</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                    Snaps <code className="font-mono">fund_balance</code> and every member's <code className="font-mono">coin_balances</code> to the immutable ledger. The <code className="font-mono">fund_transactions</code> rows are NOT touched. Logged to audit.
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="destructive"
                  className="rounded-full shrink-0"
                  onClick={onAutoFix}
                  disabled={!!autoFixBusy}
                  data-testid="drift-auto-fix-btn"
                >
                  {autoFixBusy ? (
                    <><RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> Fixing…</>
                  ) : (
                    <><Wrench className="w-3.5 h-3.5 mr-1" /> Auto-fix now</>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Fund drift summary */}
          {Math.abs(fundDrift) > 0.5 && (
            <div className="pt-3">
              <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-1.5">
                Fund ledger
              </p>
              <div className="text-sm bg-background border rounded-lg p-3">
                <p>
                  Stored fund balance differs from the expected replay by{' '}
                  <span className="font-bold text-red-700">{fmt(fundDrift)}</span>.
                </p>
                {refundRowCount > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Found <span className="font-bold">{refundRowCount}</span> "Refunded to …" row{refundRowCount === 1 ? '' : 's'} in this period — those are the most common drift sources from member-fronted expenses (iter147).
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Per-member drift list */}
          {driftedMembers.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-1.5">
                Members with balance drift
              </p>
              <ul className="space-y-1">
                {driftedMembers.slice(0, 8).map((m) => (
                  <li key={m.user_id}>
                    <button
                      type="button"
                      onClick={() => onJumpToMember(m.user_id)}
                      data-testid={`drift-trace-member-${m.user_id}`}
                      className="w-full flex items-center justify-between gap-3 text-left px-3 py-2 rounded-lg border bg-background hover:border-red-500/40 hover:bg-red-500/5 transition-colors"
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                        <span className="font-medium truncate">{m.user_name}</span>
                      </span>
                      <span className="text-xs tabular-nums shrink-0">
                        <span className={`font-bold ${m.drift < 0 ? 'text-red-600' : 'text-amber-600'}`}>
                          {m.drift > 0 ? '+' : '−'}{fmt(m.drift)}
                        </span>
                        <ChevronRight className="w-3 h-3 inline ml-1 opacity-60" />
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              {driftedMembers.length > 8 && (
                <p className="text-xs text-muted-foreground mt-1">
                  +{driftedMembers.length - 8} more drifted member(s) in the sidebar.
                </p>
              )}
            </div>
          )}

          {/* Diagnostic hint */}
          <div className="text-xs text-muted-foreground bg-background/50 border rounded-lg p-2.5 leading-relaxed">
            <p className="font-semibold mb-0.5">Most common causes:</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Personal-fronted expenses reimbursed from the fund (fixed in iter147 — redeploy if not already done).</li>
              <li>Manual <code className="font-mono">coin_balances</code> edits that bypassed the ledger.</li>
              <li>A backup restore without a recompute of <code className="font-mono">coin_balances</code>.</li>
              <li>An older code path with non-idempotent fine/loan flows.</li>
            </ul>
          </div>
        </div>
      )}
    </motion.div>
  );
}
